import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { NotificationService } from 'src/app/services/notification.service';
import { ExampleFlatNode, Folder } from 'src/typings';
import { environment } from 'src/environments/environment';
import { CreateComponent } from '../create/create.component';
import { FolderService } from '../folder.service';
import { FormControl } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';

@Component({
  selector: 'app-folder-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ListComponent implements OnDestroy {
  @Output() clearEvent: EventEmitter<void> = new EventEmitter();

  constructor(
    private folderService: FolderService,
    private dialog: MatDialog,
    private notificationService: NotificationService
  ) {
    this.updateFolders();
    this.folderService.foldersUpdatedGw.subscribe((folders) => {
      this.dataSource.data = folders;
    });
    this.searchSubscription = this.searchControl.valueChanges.subscribe((value) => {
      this.dataSource.data = this.allFolders.filter((folder) => {
        return this.filterFolder(folder, value);
      });
      this.treeControl.expandAll();
      if (value === '') this.treeControl.collapseAll();
    });
  }

  filterFolder(folder: Folder, search: string | null): boolean {
    if (!search) return true;
    if (folder.description.toUpperCase().includes(search.toUpperCase())) {
      return true;
    } else if (folder.children.length > 0) {
      const savedChildren = folder.children.filter((_folder) => {
        return this.filterFolder(_folder, search);
      });
      if (savedChildren.length > 0) {
        return true;
      }
    }
    return false;
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  clear() {
    this.searchControl.setValue('');
    this.clearEvent.emit();
    this.collapse();
  }

  collapse() {
    this.treeControl.collapseAll();
    return;
  }

  focusFolder: ExampleFlatNode | undefined;

  selectedFolder: ExampleFlatNode | undefined;

  searchControl: FormControl<string | null> = new FormControl('');

  searchSubscription: Subscription;

  allFolders: Folder[] = [];

  @ViewChild('fileInput') fileInput: ElementRef | undefined;

  @Output() toggleEvent: EventEmitter<ExampleFlatNode> = new EventEmitter();

  menuTopLeftPosition = { x: '0', y: '0' };

  @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger: MatMenuTrigger | undefined;

  private _transformer = (node: Folder, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.description,
      id: node.id,
      level: level,
      image: node.image
    };
  };

  treeControl = new FlatTreeControl<ExampleFlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: ExampleFlatNode) => node.expandable;

  toggle(folder: ExampleFlatNode) {
    this.toggleEvent.emit(folder);
    this.selectedFolder = folder;
  }

  async updateFolders() {
    const folders = await firstValueFrom(this.folderService.getNestedListFolders());
    this.dataSource.data = folders;
    this.allFolders = folders;
  }

  createFolder(folder?: ExampleFlatNode) {
    const dialogRef = this.dialog.open(CreateComponent);
    dialogRef.componentInstance.parent_folder = folder;
    dialogRef.componentInstance.resultEvent.subscribe((value) => {
      if (value === true) dialogRef.close();
    });
  }

  editFolder(folder: ExampleFlatNode) {
    const dialogRef = this.dialog.open(CreateComponent);
    dialogRef.componentInstance.folder = folder;
    dialogRef.componentInstance.resultEvent.subscribe((value) => {
      if (value === true) dialogRef.close();
    });
  }

  async handleFileSelect(event: any) {
    if (!this.focusFolder) return;
    const file: File = event.target.files.item(0);
    const img = await this.folderService.uploadImage(file);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const updatedFolder = await this.folderService // @todo
      .updateFolder({
        id: this.focusFolder.id,
        image: img
      })
      .toPromise();
    this.focusFolder.image = img;
    return;
  }

  updateImage(folder: ExampleFlatNode) {
    if (!this.fileInput) return;
    this.fileInput.nativeElement.click();
    this.focusFolder = folder;
    return;
  }

  getImgSrc(image: string) {
    return `${environment.BACKEND}/image/${image}`;
  }

  onRightClick(event: MouseEvent, item: ExampleFlatNode) {
    if (!this.matMenuTrigger) return;
    // preventDefault avoids to show the visualization of the right-click menu of the browser
    event.preventDefault();

    // we record the mouse position in our object
    this.menuTopLeftPosition.x = event.clientX + 'px';
    this.menuTopLeftPosition.y = event.clientY + 'px';

    // we open the menu
    // we pass to the menu the information about our object
    this.matMenuTrigger.menuData = { item: item };

    // we open the menu
    this.matMenuTrigger.openMenu();
  }

  delete(folder: ExampleFlatNode) {
    this.folderService.delete(folder.id).subscribe(() => {
      this.notificationService.notice(`Папка ${folder.name} удалена!`);
    });
  }
}
